import { queryOptions, useMutation, useSuspenseQuery } from '@tanstack/react-query';

import type { Estate } from '@/features/estates-property/api/estate';
import type { MutationConfig, QueryConfig } from '@/lib/react-query';
import type { LoaderProps } from '@/lib/router';
import { api, extractData } from '@/lib/treaty';

export type DigitalTwin = NonNullable<
  Awaited<ReturnType<ReturnType<typeof api.estates>['digital-twins']['get']>>['data']
>['digitalTwins'][number];

export type RoomPlan = DigitalTwin['roomPlans'][number];
export type PresignedUrl = RoomPlan['presignedUrls'][number];
export type AdditionalData = {
  settings?: boolean;
  showMeasurements?: boolean;
  showRoomArea?: boolean;
  roundRoomArea?: boolean;
  showFurniture?: boolean;
  showCompass?: boolean;
  showRoofs?: boolean;
  showFocusPoints?: boolean;
  showExterior?: boolean;
  transparentWalls3d?: boolean;
  snappingActive?: boolean;
  editingMode: 'view' | 'limited';
};

export type DigitalTwinsQueryOptions = {
  queryConfig?: QueryConfig<typeof getDigitalTwins>;
  id: Estate['id'];
};

type ShareDigiTwinOptions = {
  mutationConfig?: MutationConfig<typeof shareDigiTwin>;
};

type UpdateDigiTwinPresignedUrlOptions = {
  mutationConfig?: MutationConfig<typeof updateDigiTwinPresignedUrl>;
};

export const getDigitalTwins = (id: Estate['id']) => {
  return extractData(api.estates({ id })['digital-twins'].get());
};

export const getDigitalTwinsOptions = (id: Estate['id']) => ({
  queryKey: ['digital-twins', id],
  queryFn: () => getDigitalTwins(id),
});

export const getDigitalTwinsQueryOptions = (id: Estate['id']) => {
  return queryOptions(getDigitalTwinsOptions(id));
};

export const ensureDigitalTwinsQueryData = ({ context: { queryClient }, params }: LoaderProps) => {
  return queryClient.ensureQueryData(getDigitalTwinsQueryOptions(params.estateId));
};

export const useDigitalTwins = ({ queryConfig, id }: DigitalTwinsQueryOptions) => {
  return useSuspenseQuery({ ...queryConfig, ...getDigitalTwinsQueryOptions(id) });
};

const shareDigiTwin = ({
  id,
  planId,
  expiresIn,
  name,
  additionalData,
}: {
  id: string;
  planId: string;
  expiresIn?: number;
  name?: string;
  additionalData?: AdditionalData;
}) => {
  return extractData(
    api['digital-twins']({ id })
      ['room-plan']({ planId })
      .share.post({ expiresIn, name, additionalData })
  );
};

export const useShareDigiTwin = ({ mutationConfig }: ShareDigiTwinOptions) => {
  return useMutation({
    ...mutationConfig,
    mutationFn: shareDigiTwin,
  });
};

const updateDigiTwinPresignedUrl = ({
  id,
  token,
  expiresIn,
  name,
  additionalData,
}: {
  id: string;
  token: string;
  expiresIn?: number;
  name?: string;
  additionalData?: AdditionalData;
}) => {
  return extractData(
    api['digital-twins']({ id })['shared-links']({ token }).put({ expiresIn, name, additionalData })
  );
};

export const useUpdateDigiTwinPresignedUrl = ({
  mutationConfig,
}: UpdateDigiTwinPresignedUrlOptions) => {
  return useMutation({
    ...mutationConfig,
    mutationFn: updateDigiTwinPresignedUrl,
  });
};
